import moment from 'moment';
import angular from 'angular';

class LocationModalController {
  constructor(AuthenticationService, $http, $uibModalInstance, $rootScope, $window, $timeout, toastr, billableTrip, existingLocation) {
    'ngInject';

    this.$http = $http;
    this.$uibModalInstance = $uibModalInstance;
    this.$rootScope = $rootScope;
    this.$window = $window;
    this.$timeout = $timeout;
    this.toastr = toastr;
    this.billableTrip = billableTrip !== false;
    this.editMode = !!(existingLocation && existingLocation.id);
    this.originalLocation = this.editMode ? existingLocation : null;

    this.location = {
      itinerary: true
    };
    this.locationSearch = '';
    this.destinationSelected = false;
    this.needsZipCode = false;
    this.candidateZips = [];
    this.rateCandidates = [];
    this.selectedZip = '';
    this.selectedZipIndex = '';
    this.selectedRateCandidateId = '';
    this.loadingZips = false;
    this.lookupMessage = null;
    this.lookingUp = false;
    this.dateRangeError = null;
    this.datePickerFormat = 'MM-dd-yyyy';
    this.fromDateOpen = false;
    this.toDateOpen = false;
    this.baseDatePickerOptions = {
      formatYear: 'yy',
      startingDay: 1,
      showWeeks: false
    };
    this.fromDatePickerOptions = angular.copy(this.baseDatePickerOptions);
    this.toDatePickerOptions = angular.copy(this.baseDatePickerOptions);

    // Allow CONUS + OCONUS/foreign localities (rates come from admin-imported DoD data).
    this.locationAutocompleteOptions = {
      types: ['(regions)']
    };

    if (this.editMode) {
      this.hydrateExistingLocation(existingLocation);
    }
  }

  hydrateExistingLocation(existingLocation) {
    this.location = {
      itinerary: true,
      id: existingLocation.id,
      place: existingLocation.place,
      city: existingLocation.city,
      state: existingLocation.state,
      country: existingLocation.country,
      zip: existingLocation.zip,
      fromDate: existingLocation.fromDate ? moment(existingLocation.fromDate).toDate() : null,
      toDate: existingLocation.toDate ? moment(existingLocation.toDate).toDate() : null,
      daysTraveled: existingLocation.daysTraveled,
      mealsRate: existingLocation.mealsRate,
      lodgingRate: existingLocation.lodgingRate,
      actualLodging: existingLocation.actualLodging,
      paymentMethod: existingLocation.paymentMethod,
      receipts: existingLocation.receipts
    };
    this.locationSearch = existingLocation.place || '';
    this.destinationSelected = !!existingLocation.place;
    this.needsZipCode = false;
    this.candidateZips = [];
    this.rateCandidates = [];
    this.selectedZip = existingLocation.zip ? String(existingLocation.zip) : '';
    this.selectedZipIndex = '';
    this.selectedRateCandidateId = '';
    this.loadingZips = false;
    this.lookupMessage = null;
    this.updateDatePickerLimits();
  }

  getModalTitle() {
    return this.editMode ? 'Edit Travel Location' : 'Add Travel Location';
  }

  getSubmitLabel() {
    return this.editMode ? 'Save Location' : 'Add Location';
  }

  openDatePicker($event, field) {
    $event.preventDefault();
    $event.stopPropagation();

    if (field === 'from') {
      this.fromDateOpen = !this.fromDateOpen;
      this.toDateOpen = false;
      return;
    }

    this.toDateOpen = !this.toDateOpen;
    this.fromDateOpen = false;
  }

  handleTravelDatesChange(changedField) {
    if (!this.location.fromDate || !this.location.toDate) {
      this.dateRangeError = null;
      this.updateDatePickerLimits();
      return;
    }

    const from = moment(this.location.fromDate).startOf('day');
    const to = moment(this.location.toDate).startOf('day');

    if (from.isAfter(to)) {
      if (changedField === 'from') {
        this.location.toDate = new Date(this.location.fromDate.getTime());
      } else {
        this.location.fromDate = new Date(this.location.toDate.getTime());
      }
    }

    this.dateRangeError = null;
    this.updateDatePickerLimits();
  }

  updateDatePickerLimits() {
    this.fromDatePickerOptions = angular.extend({}, this.baseDatePickerOptions, {
      maxDate: this.location.toDate || null
    });
    this.toDatePickerOptions = angular.extend({}, this.baseDatePickerOptions, {
      minDate: this.location.fromDate || null
    });
  }

  hasValidTravelDates() {
    if (!this.location.fromDate || !this.location.toDate) {
      return false;
    }

    return !moment(this.location.fromDate).startOf('day').isAfter(moment(this.location.toDate).startOf('day'));
  }

  onDestinationSelected(place) {
    if (!place) {
      return;
    }

    this.destinationSelected = !!place.geometry;
    this.location.place = place.formatted_address || place.name || this.locationSearch;
    this.needsZipCode = false;
    this.candidateZips = [];
    this.rateCandidates = [];
    this.selectedZip = '';
    this.selectedZipIndex = '';
    this.selectedRateCandidateId = '';
    this.loadingZips = false;
    this.lookupMessage = null;

    const components = this.extractAddressComponents(place);
    this.location.city = components.city;
    this.location.state = components.state;
    this.location.country = components.country;
    this.location.zip = components.zip || null;
    if (this.location.zip) {
      this.setSelectedZip(this.location.zip);
    }

    if (this.location.zip || !this.billableTrip) {
      return;
    }

    // CONUS city/state without a zip: show the dropdown immediately and load options.
    if (this.isConusLocation() && this.location.city && this.location.state) {
      this.needsZipCode = true;
      this.ensureCandidateZips();
    }

    if (place.place_id) {
      this.resolvePostalCodeFromPlaceId(place.place_id).then((resolvedZip) => {
        this.$timeout(() => {
          if (!resolvedZip || this.hasSelectedZip()) {
            return;
          }
          if (this.candidateZips.indexOf(String(resolvedZip)) === -1) {
            this.candidateZips = [String(resolvedZip)].concat(this.candidateZips);
          }
          this.setSelectedZip(resolvedZip);
        });
      });
    }
  }

  extractAddressComponents(place) {
    const result = {
      city: null,
      state: null,
      country: null,
      zip: null
    };

    if (!place || !place.address_components) {
      return result;
    }

    let locality = null;
    let postalTown = null;
    let adminArea2 = null;
    let adminArea1 = null;
    let country = null;
    let postal = null;

    place.address_components.forEach((component) => {
      const types = component.types || [];
      if (types.includes('locality')) {
        locality = component.long_name;
      }
      if (types.includes('postal_town')) {
        postalTown = component.long_name;
      }
      if (types.includes('administrative_area_level_2')) {
        adminArea2 = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        adminArea1 = component.short_name || component.long_name;
      }
      if (types.includes('country')) {
        country = component.short_name === 'US' ? 'UNITED STATES' : (component.long_name || component.short_name);
      }
      if (types.includes('postal_code')) {
        postal = component.short_name;
      }
    });

    result.city = locality || postalTown || adminArea2 || place.name || null;
    result.state = adminArea1;
    result.country = country;
    result.zip = postal ? String(postal).substring(0, 5) : null;
    return result;
  }

  extractPostalCode(place) {
    if (!place || !place.address_components) {
      return null;
    }

    const postalComponent = place.address_components.find((component) =>
      component.types.includes('postal_code')
    );

    return postalComponent ? postalComponent.short_name : null;
  }

  resolvePostalCodeFromPlaceId(placeId) {
    return new Promise((resolve) => {
      if (!this.$window.google || !this.$window.google.maps) {
        resolve(null);
        return;
      }

      const geocoder = new this.$window.google.maps.Geocoder();
      geocoder.geocode({ placeId }, (results, status) => {
        if (status !== 'OK' || !results || !results.length) {
          resolve(null);
          return;
        }

        for (let i = 0; i < results.length; i++) {
          const zip = this.extractPostalCode(results[i]);
          if (zip) {
            resolve(String(zip).substring(0, 5));
            return;
          }
        }

        resolve(null);
      });
    });
  }

  isConusLocation() {
    const country = (this.location.country || '').toUpperCase();
    const state = (this.location.state || '').toUpperCase();
    const nonForeign = ['AK', 'HI', 'PR', 'GU', 'VI', 'AS', 'MP'];
    const isUs = !country || country === 'UNITED STATES' || country === 'US' || country === 'USA';
    return isUs && state && nonForeign.indexOf(state) === -1;
  }

  hasRequiredLocalityFields() {
    if (!this.location.city) {
      return false;
    }
    if (this.isConusLocation()) {
      return !!this.location.state;
    }
    return !!(this.location.country || this.location.state);
  }

  calculateDaysTraveled() {
    const fromDate = moment(this.location.fromDate).startOf('day');
    const toDate = moment(this.location.toDate).startOf('day');
    let days = 0;
    let cursor = fromDate.clone();

    while (cursor.isSameOrBefore(toDate)) {
      days++;
      cursor = cursor.add(1, 'days');
    }

    return days;
  }

  finishWithLocation() {
    if (this.editMode && this.$uibModalInstance.updateLocationInItinerary) {
      this.$uibModalInstance.updateLocationInItinerary(this.originalLocation, this.location);
    } else {
      this.$uibModalInstance.addLocationToItinerary(this.location);
    }

    this.$uibModalInstance.dismiss('success');
  }

  lookupPerDiemRates() {
    const params = {
      travelDate: moment(this.location.fromDate).format('YYYY-MM-DD')
    };
    if (this.location.city) {
      params.city = this.location.city;
    }
    if (this.location.state) {
      params.state = this.location.state;
    }
    if (this.location.country) {
      params.country = this.location.country;
    }
    const zip = this.getSelectedZip();
    if (zip) {
      params.zip = zip;
    }

    this.lookingUp = true;
    this.lookupMessage = null;

    return this.$http.get(this.$rootScope.apiHost + '/api/per-diem-rates/lookup', {
      params,
      withCredentials: true
    }).then((success) => {
      this.lookingUp = false;
      return success.data;
    }, (error) => {
      this.lookingUp = false;
      const message = (error.data && (error.data.message || error.data.error)) || 'Could not look up per diem rates.';
      this.toastr.error(message, 'Error');
      return null;
    });
  }

  applyLookupResult(result) {
    if (!result) {
      return;
    }

    if (result.matchQuality === 'exactCity' || result.matchQuality === 'standard') {
      this.location.lodgingRate = result.lodging;
      this.location.mealsRate = result.mie;
      if (result.zip && !this.hasSelectedZip()) {
        this.setSelectedZip(result.zip);
      }
      if (result.matchQuality === 'standard') {
        this.toastr.info(result.message || 'Applied CONUS standard rate.', 'Standard Rate');
      }
      this.needsZipCode = false;
      this.candidateZips = [];
      this.rateCandidates = [];
      this.selectedRateCandidateId = '';
      this.lookupMessage = null;
      this.finishWithLocation();
      return;
    }

    if (result.matchQuality === 'ambiguous') {
      this.needsZipCode = true;
      let candidates = result.candidates || [];
      const selectedZip = this.getSelectedZip();

      // If the user already chose a zip, prefer rate rows that match it.
      if (selectedZip) {
        const zipMatched = candidates.filter((candidate) =>
          candidate.zip && String(candidate.zip).substring(0, 5) === selectedZip
        );
        if (zipMatched.length) {
          candidates = zipMatched;
          const uniqueRates = {};
          zipMatched.forEach((candidate) => {
            uniqueRates[String(candidate.lodging) + '|' + String(candidate.mie)] = candidate;
          });
          const uniqueList = Object.keys(uniqueRates).map((key) => uniqueRates[key]);
          if (uniqueList.length === 1) {
            this.location.lodgingRate = uniqueList[0].lodging;
            this.location.mealsRate = uniqueList[0].mie;
            this.location.zip = selectedZip;
            this.needsZipCode = false;
            this.candidateZips = [];
            this.rateCandidates = [];
            this.lookupMessage = null;
            this.finishWithLocation();
            return;
          }
        }

        this.rateCandidates = candidates;
        this.candidateZips = (result.candidateZips || []).map((zip) => String(zip));
        this.selectedRateCandidateId = '';
        this.lookupMessage = result.message
          || 'This city has different per diem rates by county. Select the county where you will stay.';
        this.toastr.warning(this.lookupMessage, 'Select County');
        return;
      }

      this.candidateZips = (result.candidateZips || []).map((zip) => String(zip));
      this.rateCandidates = candidates;
      this.selectedRateCandidateId = '';
      this.lookupMessage = result.message
        || 'This city has different per diem rates by county. Select the county where you will stay.';
      if (candidates.length) {
        this.toastr.warning(this.lookupMessage, 'Select County');
      } else {
        this.toastr.warning(this.lookupMessage, 'Zip Code Required');
        this.ensureCandidateZips();
      }
      return;
    }

    // none
    if (this.isConusLocation() && !this.hasSelectedZip()) {
      this.needsZipCode = true;
      this.candidateZips = (result.candidateZips || []).map((zip) => String(zip));
      this.rateCandidates = result.candidates || [];
      this.selectedRateCandidateId = '';
      this.lookupMessage = result.message || 'No rate found for this city/state. Select a zip code to try again.';
      this.toastr.warning(this.lookupMessage, 'Zip Code Required');
      this.ensureCandidateZips();
      return;
    }

    this.toastr.error(result.message || 'No per diem rate found for this location. Ask an admin to import rates.', 'Rate Not Found');
  }

  ensureCandidateZips() {
    if (this.candidateZips.length || !this.location.city || !this.location.state) {
      return;
    }

    this.loadingZips = true;
    this.$http.get(this.$rootScope.apiHost + '/api/per-diem-rates/postal-codes', {
      params: {
        city: this.location.city,
        state: this.location.state
      },
      withCredentials: true
    }).then((success) => {
      this.loadingZips = false;
      this.candidateZips = (success.data || []).map((zip) => String(zip));
      const current = this.selectedZip || this.location.zip;
      if (current) {
        const normalized = String(current).trim().substring(0, 5);
        if (this.candidateZips.indexOf(normalized) === -1) {
          this.candidateZips = [normalized].concat(this.candidateZips);
        }
        this.setSelectedZip(normalized);
      }
      if (!this.candidateZips.length && !this.rateCandidates.length) {
        this.lookupMessage = 'No zip codes were found for this city/state.';
      }
    }, () => {
      this.loadingZips = false;
      if (!this.rateCandidates.length) {
        this.toastr.error('Could not load zip codes for this location.', 'Error');
      }
    });
  }

  onZipIndexSelected() {
    const index = this.selectedZipIndex === '' || this.selectedZipIndex == null
      ? -1
      : parseInt(this.selectedZipIndex, 10);
    if (isNaN(index) || index < 0 || index >= this.candidateZips.length) {
      this.selectedZip = '';
      this.location.zip = null;
      return;
    }
    this.selectedZip = String(this.candidateZips[index]);
    this.location.zip = this.selectedZip;
  }

  setSelectedZip(zip) {
    if (!zip) {
      this.selectedZip = '';
      this.selectedZipIndex = '';
      this.location.zip = null;
      return;
    }
    const normalized = String(zip).trim().substring(0, 5);
    this.selectedZip = normalized;
    this.location.zip = normalized;
    const index = this.candidateZips.indexOf(normalized);
    this.selectedZipIndex = index >= 0 ? String(index) : '';
  }

  getSelectedZip() {
    if (this.selectedZipIndex !== '' && this.selectedZipIndex != null) {
      const index = parseInt(this.selectedZipIndex, 10);
      if (!isNaN(index) && index >= 0 && index < this.candidateZips.length) {
        return String(this.candidateZips[index]).trim().substring(0, 5);
      }
    }
    const raw = this.selectedZip != null && this.selectedZip !== ''
      ? this.selectedZip
      : this.location.zip;
    if (raw == null || raw === '') {
      return '';
    }
    return String(raw).trim().substring(0, 5);
  }

  hasSelectedZip() {
    return !!this.getSelectedZip();
  }

  applySelectedRateCandidate() {
    if (!this.selectedRateCandidateId) {
      return;
    }
    const rate = (this.rateCandidates || []).find((candidate) =>
      String(candidate.id) === String(this.selectedRateCandidateId)
    );
    if (!rate) {
      return;
    }
    this.location.lodgingRate = rate.lodging;
    this.location.mealsRate = rate.mie;
    if (rate.zip) {
      this.location.zip = rate.zip;
    }
    this.needsZipCode = false;
    this.candidateZips = [];
    this.rateCandidates = [];
    this.lookupMessage = null;
    this.finishWithLocation();
  }

  formatRateMoney(value) {
    if (value == null || value === '') {
      return '—';
    }
    return '$' + Number(value).toFixed(0);
  }

  formatRateCandidateLabel(rate) {
    if (!rate) {
      return '';
    }
    const county = rate.county ? (rate.county + ' County') : (rate.city || 'Locality');
    const season = (rate.seasonStart || rate.seasonEnd)
      ? ((rate.seasonStart || '01-01') + ' to ' + (rate.seasonEnd || '12-31'))
      : 'Year-round';
    return county
      + ' · '
      + season
      + ' · Lodging '
      + this.formatRateMoney(rate.lodging)
      + ' / M&IE '
      + this.formatRateMoney(rate.mie);
  }

  addToItinerary() {
    if (!this.destinationSelected || !this.location.place || !this.location.place.trim()) {
      this.toastr.warning('Select a destination from the search suggestions.', 'Destination Required');
      return;
    }

    if (this.billableTrip && !this.hasRequiredLocalityFields()) {
      if (this.isConusLocation() || !this.location.country) {
        this.toastr.warning('A city and state are required to calculate per diem rates.', 'Location Incomplete');
      } else {
        this.toastr.warning('A city and country are required to calculate per diem rates.', 'Location Incomplete');
      }
      return;
    }

    // Keep location.zip in sync with the dropdown model before validating/looking up.
    this.onZipIndexSelected();

    if (this.billableTrip && this.needsZipCode && !this.hasSelectedZip() && !this.selectedRateCandidateId) {
      if (this.rateCandidates.length) {
        this.toastr.warning('Select the county where you will stay.', 'Select County');
      } else {
        this.toastr.warning('Select a zip code to resolve per diem rates.', 'Zip Code Required');
      }
      return;
    }

    if (!this.hasValidTravelDates()) {
      this.dateRangeError = 'The from date cannot be after the to date.';
      this.toastr.warning('Select a valid travel date range.', 'Invalid Dates');
      return;
    }

    this.location.daysTraveled = this.calculateDaysTraveled();
    this.location.fromDate = moment(this.location.fromDate).startOf('day').toDate();
    this.location.toDate = moment(this.location.toDate).startOf('day').toDate();

    if (!this.billableTrip) {
      this.location.mealsRate = 0;
      this.location.lodgingRate = 0;
      this.finishWithLocation();
      return;
    }

    if (this.selectedRateCandidateId) {
      this.applySelectedRateCandidate();
      return;
    }

    this.lookupPerDiemRates().then((result) => {
      this.applyLookupResult(result);
    });
  }

  cancel() {
    this.$uibModalInstance.dismiss('cancel');
  }
}

export default LocationModalController;



// WEBPACK FOOTER //
// ./client/pages/expenses/modals/location/location.controller.js